import { Combobox } from "@foldkit/ui";
import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import {
	approvePendingSend,
	createAgentLink,
	denyPendingSend,
	fetchAgents,
	promoteContext,
	revokeAgentLink,
	revokeContext,
} from "../../api/commands/agents";
import { createApiKey } from "../../api/commands/integrations";
import { setRecordAgentLink } from "../../api/commands/records";
import { type ApiError, toMessage } from "../../api/errors";
import { type AgentsData, UNDELIVERED } from "../../api/schemas/agents";
import { i18next } from "../../i18n";
import { Dialog } from "../../widgets/dialog";
import * as Picker from "../../widgets/recordPicker";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { type Model, type PendingWrite, WriteKind } from "./model";

/**
 * Agent activity: the inbox, sent feed and shared contexts are not
 * time-versioned, so this page follows keepInTouch (KTD8): every fetch is
 * generation-checked and always shows live data. While `Shared.asOf` is set
 * the view hides promote/revoke and shows a one-line live-data notice, and
 * the update ignores those clicks too.
 */

// COMMANDS

export const FetchAgents = Command.define("FetchAgents", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetch],
	execute: ({ generation }) =>
		fetchAgents.pipe(
			Effect.map((result) => Message.CompletedFetch({ generation, result })),
		),
});

/** How often the open page refetches, so countdowns and "Expires next" stay true. */
export const REFRESH_INTERVAL = "1 minute";

export const RefreshAgents = Command.define("RefreshAgents", {
	args: { generation: S.Number, after: S.Number },
	messages: [Message.CompletedRefresh],
	execute: ({ generation, after }) =>
		Effect.sleep(REFRESH_INTERVAL).pipe(
			Effect.andThen(fetchAgents),
			Effect.map((result) =>
				Message.CompletedRefresh({ generation, after, result }),
			),
		),
});

const write = (kind: WriteKind, id: string) => {
	switch (kind) {
		case "promote":
			return promoteContext.run({ contextId: id, target: "task" });
		case "revoke":
			return revokeContext.run({ contextId: id });
		case "revokeLink":
			return revokeAgentLink.run({ id });
		case "deny":
			return denyPendingSend.run({ id });
	}
};

/** Every write but link creation; the page refetches after, so bodies are not read. */
export const WriteContext = Command.define("WriteContext", {
	args: { kind: WriteKind, id: S.String, generation: S.Number },
	messages: [Message.CompletedWrite],
	execute: ({ kind, id, generation }) =>
		write(kind, id).pipe(
			Effect.map((result) => Message.CompletedWrite({ generation, result })),
		),
});

/** Approves a held send; unlike other writes, the body is read: it says if the message went out. */
export const ApproveSend = Command.define("ApproveSend", {
	args: { id: S.String, to: S.String, generation: S.Number },
	messages: [Message.CompletedApprove],
	execute: ({ id, to, generation }) =>
		approvePendingSend
			.run({ id })
			.pipe(
				Effect.map((result) =>
					Message.CompletedApprove({ generation, to, result }),
				),
			),
});

/** Saves someone else's agent link on a person record (same endpoint as the record page). */
export const SavePastedLink = Command.define("SavePastedLink", {
	args: {
		recordId: S.String,
		name: S.String,
		url: S.String,
		generation: S.Number,
	},
	messages: [Message.CompletedPaste],
	execute: ({ recordId, name, url, generation }) =>
		setRecordAgentLink
			.run({ nodeId: recordId, url })
			.pipe(
				Effect.map((result) =>
					Message.CompletedPaste({ generation, name, result }),
				),
			),
});

/** Creates a named link; its URL comes back once, here. */
export const CreateLink = Command.define("CreateLink", {
	args: {
		agentId: S.String,
		label: S.String,
		forRecordId: S.NullOr(S.String),
		generation: S.Number,
	},
	messages: [Message.CompletedCreateLink],
	execute: ({ generation, ...args }) =>
		createAgentLink.run(args).pipe(
			Effect.map((result) =>
				Message.CompletedCreateLink({
					generation,
					label: args.label,
					result,
				}),
			),
		),
});

/** Creates the agent's API key; the server returns the key once. */
export const CreateAgentKey = Command.define("CreateAgentKey", {
	args: { name: S.String, generation: S.Number },
	messages: [Message.CompletedCreateKey],
	execute: ({ name, generation }) =>
		createApiKey
			.run({ description: name })
			.pipe(
				Effect.map((result) =>
					Message.CompletedCreateKey({ generation, name, result }),
				),
			),
});

/** Writes to the clipboard, then waits two seconds so "Copied" can clear. */
const copyThen = <M>(text: string, done: M) =>
	Effect.promise(() => navigator.clipboard.writeText(text)).pipe(
		Effect.catch(() => Effect.void),
		Effect.flatMap(() => Effect.sleep("2 seconds")),
		Effect.as(done),
	);

export const CopyKey = Command.define("CopyKey", {
	args: { key: S.String },
	messages: [Message.CopyFeedbackExpired],
	execute: ({ key }) => copyThen(key, Message.CopyFeedbackExpired()),
});

export const CopyLink = Command.define("CopyLink", {
	args: { url: S.String },
	messages: [Message.LinkCopyExpired],
	execute: ({ url }) => copyThen(url, Message.LinkCopyExpired()),
});

const initialConnect: Model["connect"] = {
	name: "",
	creating: false,
	created: Option.none(),
	error: Option.none(),
	copied: false,
};

const initialShare: Model["share"] = {
	agentId: "",
	label: "",
	picker: Picker.init,
	creating: false,
	created: Option.none(),
	error: Option.none(),
	copied: false,
};

const initialPaste: Model["paste"] = {
	// Its own combobox id: the share form's picker is on the same page.
	picker: {
		...Picker.init,
		combobox: Combobox.init({ id: "paste-record-combobox" }),
	},
	url: "",
	saving: false,
	error: Option.none(),
	saved: Option.none(),
};

export const initial: Model = {
	page: "Agents",
	data: AsyncData.Idle(),
	error: Option.none(),
	dialog: Dialog.init({ id: "confirm_agents_dialog" }),
	pending: Option.none(),
	writing: false,
	connect: initialConnect,
	share: initialShare,
	sendOutcome: Option.none(),
	paste: initialPaste,
};

type Return = PageReturn<Model, Message>;

const load = (model: Model, shared: Shared): Return => ({
	model: evo(model, { data: start }),
	commands: [FetchAgents({ generation: shared.generation })],
});

/** Settle a fetch and, on success, schedule the next refresh after it. */
const settleAndSchedule = (
	model: Model,
	shared: Shared,
	result: Result.Result<AgentsData, ApiError>,
): Return => ({
	model: evo(model, { data: (data) => settle(data, result) }),
	commands: Result.match(result, {
		onSuccess: ({ now }) => [
			RefreshAgents({ generation: shared.generation, after: now }),
		],
		onFailure: () => [],
	}),
});

const dialogChild = {
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
};
const foldDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	...dialogChild,
});
const openDialog = Update.foldChildStep({
	update: Dialog.open,
	...dialogChild,
});
const closeDialog = Update.foldChildStep({
	update: Dialog.close,
	...dialogChild,
});

const ask = (model: Model, shared: Shared, pending: PendingWrite): Return =>
	Option.isSome(shared.asOf)
		? { model }
		: openDialog(
				evo(model, {
					pending: () => Option.some(pending),
					error: () => Option.none(),
				}),
			);

/** Approve or deny a held send: no dialog, one at a time, never under asOf. */
const decide = (
	model: Model,
	shared: Shared,
	kind: "approve" | "deny",
	id: string,
): Return => {
	if (Option.isSome(shared.asOf) || model.writing) return { model };
	const to =
		Option.getOrUndefined(AsyncData.getData(model.data))?.pendingSends.find(
			(p) => p.id === id,
		)?.to ?? "";
	return {
		model: evo(model, {
			writing: () => true,
			error: () => Option.none(),
			sendOutcome: () => Option.none(),
		}),
		commands: [
			kind === "approve"
				? ApproveSend({ id, to, generation: shared.generation })
				: WriteContext({ kind, id, generation: shared.generation }),
		],
	};
};

const connect = (model: Model, patch: Partial<Model["connect"]>): Model =>
	evo(model, { connect: (c) => ({ ...c, ...patch }) });

const share = (model: Model, patch: Partial<Model["share"]>): Model =>
	evo(model, { share: (s) => ({ ...s, ...patch }) });

const paste = (model: Model, patch: Partial<Model["paste"]>): Model =>
	evo(model, { paste: (p) => ({ ...p, ...patch }) });

const foldPastePicker = (shared: Shared) =>
	Update.foldChild({
		update: (child: Picker.Model, message: Picker.Message) => {
			const next = Picker.update(child, message, shared);
			// Picker's Clear resets to its default combobox id; keep this one distinct.
			return next.model.combobox.id === child.combobox.id
				? next
				: {
						...next,
						model: {
							...next.model,
							combobox: Combobox.init({ id: child.combobox.id }),
						},
					};
		},
		read: (model: Model) => Option.some(model.paste.picker),
		write: (model: Model, picker: Picker.Model) =>
			paste(model, { picker, error: Option.none(), saved: Option.none() }),
		toParentMessage: (message: Picker.Message) =>
			Message.GotPastePickerMessage({ message }),
	});

const pasteError = (error: ApiError): string =>
	error._tag === "Http" && error.status === 400
		? i18next.t("agents.paste.invalidUrl")
		: error._tag === "NotFound"
			? i18next.t("agents.paste.notPerson")
			: writeError(error);

const foldPicker = (shared: Shared) =>
	Update.foldChild({
		update: (child: Picker.Model, message: Picker.Message) =>
			Picker.update(child, message, shared),
		read: (model: Model) => Option.some(model.share.picker),
		write: (model: Model, picker: Picker.Model) => share(model, { picker }),
		toParentMessage: (message: Picker.Message) =>
			Message.GotPickerMessage({ message }),
	});

/** The agent the share form targets: the picked one, else the first. */
export const shareAgentId = (model: Model): string =>
	model.share.agentId ||
	(Option.getOrUndefined(AsyncData.getData(model.data))?.keys[0]?.id ?? "");

const writeError = (error: ApiError): string =>
	error._tag === "Http" && error.status === 403
		? i18next.t("agents.error.forbidden")
		: i18next.t("agents.error.write", { message: toMessage(error) });

export const page: Page<Model, Message> = {
	init: (_route, shared) => load(initial, shared),
	// Nothing on the route changes what is fetched (KTD8).
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetch: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: settleAndSchedule(model, shared, result),
			// Only the loop that follows the data on screen continues; a failed
			// refresh keeps the data (Stale) and tries again next interval.
			CompletedRefresh: ({ generation, after, result }) =>
				generation !== shared.generation ||
				AsyncData.isPending(model.data) ||
				Option.getOrUndefined(AsyncData.getData(model.data))?.now !== after
					? { model }
					: Result.isSuccess(result)
						? settleAndSchedule(model, shared, result)
						: {
								model: evo(model, { data: (data) => settle(data, result) }),
								commands: [
									RefreshAgents({ generation: shared.generation, after }),
								],
							},
			ClickedPromote: ({ contextId, title }) =>
				ask(model, shared, { kind: "promote", id: contextId, title }),
			ClickedRevoke: ({ contextId, title }) =>
				ask(model, shared, { kind: "revoke", id: contextId, title }),
			ClickedRevokeLink: ({ id, label }) =>
				ask(model, shared, { kind: "revokeLink", id, title: label }),
			ClickedApprove: ({ id }) => decide(model, shared, "approve", id),
			ClickedDeny: ({ id }) => decide(model, shared, "deny", id),
			ClickedConfirm: () => {
				const closed = closeDialog(
					evo(model, { pending: () => Option.none() }),
				);
				return Option.match(model.pending, {
					onNone: () => closed,
					onSome: ({ kind, id }) => ({
						model: evo(closed.model, { writing: () => true }),
						commands: [
							...(closed.commands ?? []),
							WriteContext({ kind, id, generation: shared.generation }),
						],
					}),
				});
			},
			GotDialogMessage: ({ message }) => foldDialog(model, message),
			ChangedAgentName: ({ value }) => ({
				model: connect(model, { name: value, error: Option.none() }),
			}),
			SubmittedConnect: () => {
				const name = model.connect.name.trim();
				return Option.isSome(shared.asOf) || model.connect.creating || !name
					? { model }
					: {
							model: connect(model, { creating: true, error: Option.none() }),
							commands: [
								CreateAgentKey({ name, generation: shared.generation }),
							],
						};
			},
			CompletedCreateKey: ({ generation, name, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: ({ id, key }) =>
								load(
									share(
										connect(model, {
											creating: false,
											name: "",
											created: Option.some({ name, key }),
										}),
										{ agentId: id },
									),
									shared,
								),
							onFailure: (error) => ({
								model: connect(model, {
									creating: false,
									error: Option.some(writeError(error)),
								}),
							}),
						}),
			ClickedCopyKey: () =>
				Option.match(model.connect.created, {
					onNone: () => ({ model }),
					onSome: ({ key }) => ({
						model: connect(model, { copied: true }),
						commands: [CopyKey({ key })],
					}),
				}),
			CopyFeedbackExpired: () => ({ model: connect(model, { copied: false }) }),
			// The key is never shown again once dismissed.
			ClickedDoneConnect: () => ({
				model: connect(model, { created: Option.none(), copied: false }),
			}),
			ChangedShareAgent: ({ value }) => ({
				model: share(model, { agentId: value, error: Option.none() }),
			}),
			ChangedShareLabel: ({ value }) => ({
				model: share(model, { label: value, error: Option.none() }),
			}),
			GotPickerMessage: ({ message }) => foldPicker(shared)(model, message),
			SubmittedShare: () => {
				const label = model.share.label.trim();
				const agentId = shareAgentId(model);
				return Option.isSome(shared.asOf) ||
					model.share.creating ||
					!label ||
					!agentId
					? { model }
					: {
							model: share(model, { creating: true, error: Option.none() }),
							commands: [
								CreateLink({
									agentId,
									label,
									forRecordId: Option.match(model.share.picker.chosen, {
										onNone: () => null,
										onSome: ({ id }) => id,
									}),
									generation: shared.generation,
								}),
							],
						};
			},
			CompletedCreateLink: ({ generation, label, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: ({ url }) =>
								load(
									share(model, {
										...initialShare,
										agentId: model.share.agentId,
										created: Option.some({ label, url }),
									}),
									shared,
								),
							onFailure: (error) => ({
								model: share(model, {
									creating: false,
									error: Option.some(writeError(error)),
								}),
							}),
						}),
			ClickedCopyLink: () =>
				Option.match(model.share.created, {
					onNone: () => ({ model }),
					onSome: ({ url }) => ({
						model: share(model, { copied: true }),
						commands: [CopyLink({ url })],
					}),
				}),
			LinkCopyExpired: () => ({ model: share(model, { copied: false }) }),
			// The URL is never shown again once dismissed.
			ClickedDoneShare: () => ({
				model: share(model, { created: Option.none(), copied: false }),
			}),
			CompletedApprove: ({ generation, to, result }) => {
				if (generation !== shared.generation) return { model };
				const settled = evo(model, { writing: () => false });
				return Result.match(result, {
					onSuccess: ({ sent }) =>
						load(
							evo(settled, {
								sendOutcome: () =>
									Option.some({
										to,
										error:
											sent.error !== undefined ||
											UNDELIVERED.includes(sent.status)
												? Option.some(sent.error ?? sent.status)
												: Option.none(),
									}),
							}),
							shared,
						),
					onFailure: (error) => ({
						model: evo(settled, {
							error: () => Option.some(writeError(error)),
						}),
					}),
				});
			},
			ClickedDismissOutcome: () => ({
				model: evo(model, { sendOutcome: () => Option.none() }),
			}),
			GotPastePickerMessage: ({ message }) =>
				foldPastePicker(shared)(model, message),
			ChangedPasteUrl: ({ value }) => ({
				model: paste(model, {
					url: value,
					error: Option.none(),
					saved: Option.none(),
				}),
			}),
			SubmittedPaste: () => {
				const url = model.paste.url.trim();
				if (Option.isSome(shared.asOf) || model.paste.saving || !url)
					return { model };
				return Option.match(model.paste.picker.chosen, {
					onNone: () => ({
						model: paste(model, {
							picker: Picker.requireChoice(model.paste.picker),
						}),
					}),
					onSome: ({ id, name }) => ({
						model: paste(model, { saving: true, error: Option.none() }),
						commands: [
							SavePastedLink({
								recordId: id,
								name,
								url,
								generation: shared.generation,
							}),
						],
					}),
				});
			},
			CompletedPaste: ({ generation, name, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: () =>
								load(
									evo(model, {
										paste: () => ({
											...initialPaste,
											saved: Option.some(name),
										}),
									}),
									shared,
								),
							onFailure: (error) => ({
								model: paste(model, {
									saving: false,
									error: Option.some(pasteError(error)),
								}),
							}),
						}),
			CompletedWrite: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				const settled = evo(model, { writing: () => false });
				return Result.match(result, {
					onSuccess: () => load(settled, shared),
					onFailure: (error) => ({
						model: evo(settled, {
							error: () => Option.some(writeError(error)),
						}),
					}),
				});
			},
		}),
};
